import { useCallback, useEffect, useState } from 'react';
import { Form } from 'react-aria-components';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import { q } from '@actual-app/core/shared/query';
import type { TransactionEntity } from '@actual-app/core/types/models';
import type { TransObjectLiteral } from '@actual-app/core/types/util';

import {
  deleteFieldMapping,
  getFieldMappings,
  getPreviewTransactions,
  reapplyFieldMappings,
  saveFieldMapping,
  testFieldMapping,
} from '#banksync/field-mappings';
import type { FieldMapping } from '#banksync/field-mappings';
import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';
import type { Modal as ModalType } from '#modals/modalsSlice';
import { aqlQuery } from '#queries/aqlQuery';

type BankFieldMappingModalProps = Extract<
  ModalType,
  { name: 'bank-field-mapping' }
>['options'];

const FIELD_OPTIONS: Array<readonly [string, string]> = [
  ['payeeName', 'Payee Name'],
  ['notes', 'Notes'],
  ['date', 'Date'],
];

const EXAMPLE_TEMPLATES: Record<string, string> = {
  payeeName: "it.creditor?.name || it.debtor?.name || 'Unknown'",
  notes: "it.remittance_information?.join(' | ') || ''",
  date: 'it.transaction_date || it.booking_date || it.value_date || ""',
};

type PreviewTransaction = {
  transaction_date?: string;
  booking_date?: string;
  value_date?: string;
  creditor?: { name?: string };
  debtor?: { name?: string };
  transaction_amount?: { amount?: string; currency?: string };
};

export function BankFieldMappingModal({
  account_id,
  bank_name,
  actual_account_id,
}: BankFieldMappingModalProps) {
  const { t } = useTranslation();
  const [mappings, setMappings] = useState<FieldMapping[]>([]);
  const [selectedField, setSelectedField] = useState<string>('payeeName');
  const [template, setTemplate] = useState<string>('');
  const [testResult, setTestResult] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [reapplyLoading, setReapplyLoading] = useState(false);
  const [reapplyResult, setReapplyResult] = useState<string>('');
  const [previewTransactions, setPreviewTransactions] = useState<
    Record<string, unknown>[]
  >([]);
  const [selectedTransactionIdx, setSelectedTransactionIdx] =
    useState<number>(0);

  const loadMappings = useCallback(async () => {
    if (!account_id) return;
    try {
      // Mappings are stored keyed by the EnableBanking account id.
      const data = await getFieldMappings(account_id);
      setMappings(data);

      const existingMapping = data.find(m => m.field_name === selectedField);
      if (existingMapping) {
        setTemplate(existingMapping.eta_template);
      } else {
        setTemplate(EXAMPLE_TEMPLATES[selectedField] || '');
      }
    } catch (err) {
      setError(
        t('Failed to load field mappings: {{error}}', { error: String(err) }),
      );
    }
  }, [account_id, selectedField, t]);

  useEffect(() => {
    if (account_id) {
      void loadMappings();
    }
  }, [account_id, loadMappings]);

  useEffect(() => {
    if (account_id) {
      getPreviewTransactions(account_id)
        .then(transactions => {
          setPreviewTransactions(transactions as Record<string, unknown>[]);
        })
        .catch(() => {
          // Preview is best-effort | swallow failures so the modal still works.
        });
    }
  }, [account_id]);

  const handleFieldChange = (field: string) => {
    setSelectedField(field);
    setTestResult('');
    setError('');

    const existing = mappings.find(m => m.field_name === field);
    if (existing) {
      setTemplate(existing.eta_template);
    } else {
      setTemplate(EXAMPLE_TEMPLATES[field] || '');
    }
  };

  const handleTest = async () => {
    if (!template) {
      setError(t('Please enter a template'));
      return;
    }

    setLoading(true);
    setError('');

    const testTransaction =
      previewTransactions.length > 0
        ? previewTransactions[selectedTransactionIdx]
        : {
            creditor: { name: 'Sample Creditor' },
            debtor: { name: 'Sample Debtor' },
            remittance_information: [
              'Payment for invoice 123',
              'Reference: ABC',
            ],
            transaction_amount: { amount: '42.50', currency: 'EUR' },
            transaction_date: '2026-01-11',
            booking_date: '2026-01-10',
            value_date: '2026-01-11',
            transaction_id: 'TXN123456',
          };

    try {
      const result = await testFieldMapping(template, testTransaction);
      if (result.success) {
        setTestResult(result.result || '');
      } else {
        setError(result.error || t('Test failed'));
      }
    } catch (err) {
      setError(t('Failed to test template: {{error}}', { error: String(err) }));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!template) {
      setError(t('Please enter a template'));
      return;
    }
    if (!account_id) {
      setError(t('Cannot save: Enable Banking account ID is not available'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const existing = mappings.find(m => m.field_name === selectedField);
      // Always store mappings under the EnableBanking account_id (bank_id).
      const result = await saveFieldMapping({
        id: existing?.id,
        bank_id: account_id,
        field_name: selectedField,
        eta_template: template,
        enabled: true,
      });

      if (result.success) {
        await loadMappings();
        setError('');
        setTestResult(t('Saved successfully!'));
      } else {
        setError(result.validation_error || t('Failed to save'));
      }
    } catch (err) {
      setError(
        t('Failed to save field mapping: {{error}}', { error: String(err) }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const existing = mappings.find(m => m.field_name === selectedField);
    if (!existing) return;

    setLoading(true);
    try {
      await deleteFieldMapping(existing.id);
      await loadMappings();
      setTemplate(EXAMPLE_TEMPLATES[selectedField] || '');
      setTestResult('');
      setError('');
    } catch (err) {
      setError(
        t('Failed to delete field mapping: {{error}}', { error: String(err) }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReapply = async () => {
    if (!account_id) {
      setError(
        t('Cannot re-apply: Enable Banking account ID is not available'),
      );
      return;
    }

    if (!actual_account_id) {
      setError(t('Cannot re-apply: Actual account ID is not available'));
      return;
    }

    if (mappings.length === 0) {
      setError(t('No field mappings configured to re-apply'));
      return;
    }

    setReapplyLoading(true);
    setReapplyResult('');
    setError('');

    try {
      // The server re-fetches from the bank and re-applies the saved mappings,
      // returning the normalized payee/notes/date keyed by transactionId.
      const result = await reapplyFieldMappings(account_id, account_id);

      if (result.transactions.length === 0) {
        setReapplyResult(
          t('No transactions found from bank in the last 90 days.'),
        );
        return;
      }

      // Match the returned transactions against existing Actual transactions
      // using imported_id first, then the ids stored in raw_synced_data.
      const { data } = await aqlQuery(
        q('transactions')
          .filter({ account: actual_account_id })
          .select([
            'id',
            'imported_id',
            'imported_payee',
            'payee',
            'notes',
            'category',
            'date',
            'amount',
            'account',
            'cleared',
            'transfer_id',
            'raw_synced_data',
          ]),
      );

      const existingTransactions = data as TransactionEntity[];
      const importedIdMap = new Map<string, TransactionEntity>();
      const rawDataIdMap = new Map<string, TransactionEntity>();

      for (const trans of existingTransactions) {
        if (trans.imported_id) {
          importedIdMap.set(trans.imported_id, trans);
        }
        if (trans.raw_synced_data) {
          try {
            const rawData = JSON.parse(trans.raw_synced_data);
            const rawBankData = rawData.rawBankData
              ? JSON.parse(rawData.rawBankData)
              : null;
            // prefer entry_reference (stable) over transaction_id (not stable)
            const entryRef =
              rawData.entry_reference ?? rawBankData?.entry_reference;
            const txnId = rawData.transaction_id ?? rawBankData?.transaction_id;
            if (entryRef) rawDataIdMap.set(entryRef, trans);
            if (txnId && txnId !== entryRef) rawDataIdMap.set(txnId, trans);
          } catch {
            // skip if json parse fails
          }
        }
      }

      // Only re-apply the fields actually mapped, so reapply never clobbers an
      // unmapped field. For payee we resolve the mapped name to a real payee
      // (get-or-create, the same thing the bank-sync import does) and set the
      // visible `payee` link | writing `imported_payee` alone does not change
      // the displayed payee, which is why reapply previously did nothing.
      const hasPayeeMapping = mappings.some(m => m.field_name === 'payeeName');
      const hasNotesMapping = mappings.some(m => m.field_name === 'notes');
      const hasDateMapping = mappings.some(m => m.field_name === 'date');

      const payeesList = await send('payees-get');
      const payeeIdByName = new Map(
        payeesList.map(p => [p.name.trim().toLowerCase(), p.id]),
      );
      const resolvePayeeId = async (name: string): Promise<string> => {
        const key = name.trim().toLowerCase();
        const found = payeeIdByName.get(key);
        if (found) return found;
        const created = await send('payee-create', { name: name.trim() });
        payeeIdByName.set(key, created);
        return created;
      };

      type ReapplyUpdate = {
        id: string;
        payee?: TransactionEntity['payee'];
        imported_payee?: TransactionEntity['imported_payee'];
        notes?: TransactionEntity['notes'];
        category?: TransactionEntity['category'];
        date?: TransactionEntity['date'];
      };
      const updates: ReapplyUpdate[] = [];

      for (const refetched of result.transactions) {
        const existing =
          importedIdMap.get(refetched.transactionId) ||
          rawDataIdMap.get(refetched.transactionId);
        if (!existing) continue;

        // never re-stamp a transaction that is already linked as a transfer.
        // overwriting its transfer-payee with a plain mapped payee makes
        // loot-core's transfer.onUpdate tear down the counterpart leg
        // (removeTransfer), silently deleting the other account's transaction
        // and wrecking its balance. transfers are already meaningful | leave
        // them untouched.
        if (existing.transfer_id) continue;

        // Apply the mapped fields as the base, then run the user's rules on top
        // so rules still win (same order the bank-sync import uses: resolve
        // payee/notes first, then runRules). We never write the mapped value
        // directly past the rules.
        const base: TransactionEntity = { ...existing };
        if (hasNotesMapping) {
          base.notes = refetched.notes ?? '';
        }
        if (hasPayeeMapping && refetched.payeeName) {
          base.payee = await resolvePayeeId(refetched.payeeName);
          base.imported_payee = refetched.payeeName;
        }
        if (hasDateMapping && refetched.date) {
          base.date = refetched.date;
        }

        const ruled = await send('rules-run', { transaction: base });

        // Persist only what actually changed vs the stored transaction.
        const update: ReapplyUpdate = { id: existing.id };
        if (ruled.payee !== existing.payee) update.payee = ruled.payee;
        if (base.imported_payee !== existing.imported_payee) {
          update.imported_payee = base.imported_payee;
        }
        if (ruled.notes !== existing.notes) update.notes = ruled.notes;
        if (ruled.category !== existing.category) {
          update.category = ruled.category;
        }
        if (ruled.date !== existing.date) update.date = ruled.date;

        // nothing beyond the id changed | skip
        if (Object.keys(update).length > 1) {
          updates.push(update);
        }
      }

      if (updates.length > 0) {
        // reapply only re-stamps payee/notes/date text | it must never create
        // or tear down transfer legs, so keep loot-core's transfer handling out
        // of it (mirrors acceptTransferMatch). the transfer_id skip above is the
        // primary guard; this is defense-in-depth for any non-linked row whose
        // mapped payee happens to be a transfer payee.
        await send('transactions-batch-update', {
          updated: updates,
          runTransfers: false,
        });

        setReapplyResult(
          t(
            'Successfully updated {{count}} transaction(s) from bank data.{{errors}} ({{unmatched}} transactions had no match)',
            {
              count: updates.length,
              errors:
                result.errors.length > 0
                  ? ' ' +
                    t('{{count}} error(s) occurred.', {
                      count: result.errors.length,
                    })
                  : '',
              unmatched: result.transactions.length - updates.length,
            },
          ),
        );
      } else {
        setReapplyResult(
          t(
            'No matching transactions found. Fetched {{count}} transaction(s) from bank but none matched existing transactions.',
            { count: result.transactions.length },
          ),
        );
      }
    } catch (err) {
      setError(
        t('Failed to re-apply mappings: {{error}}', { error: String(err) }),
      );
    } finally {
      setReapplyLoading(false);
    }
  };

  return (
    <Modal name="bank-field-mapping">
      {({ state }) => (
        <>
          <ModalHeader
            title={t('Bank Field Mappings')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />
          <Form
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 15,
              padding: 20,
            }}
          >
            <View style={{ flexDirection: 'column', gap: 10 }}>
              <Text
                style={{
                  fontSize: 13,
                  color: theme.pageTextSubdued,
                  marginBottom: 5,
                }}
              >
                <Trans>
                  Bank:{' '}
                  <strong>
                    {{ account: bank_name || account_id } as TransObjectLiteral}
                  </strong>
                </Trans>
              </Text>
              <Text>
                <Trans>
                  Write JavaScript expressions to customize how bank transaction
                  fields are mapped. The transaction data is available as{' '}
                  <code>it</code>.
                </Trans>
              </Text>

              <View
                style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}
              >
                <Text style={{ fontWeight: 600, minWidth: 80 }}>
                  <Trans>Field:</Trans>
                </Text>
                <Select
                  value={selectedField}
                  onChange={handleFieldChange}
                  options={FIELD_OPTIONS}
                />
              </View>

              {previewTransactions.length > 0 && (
                <View style={{ flexDirection: 'column', gap: 5 }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ fontWeight: 600 }}>
                      <Trans>Test with Real Transaction:</Trans>
                    </Text>
                    <Button
                      variant="bare"
                      onPress={() => {
                        const json = JSON.stringify(
                          previewTransactions[selectedTransactionIdx],
                          null,
                          2,
                        );
                        void navigator.clipboard.writeText(json);
                        setTestResult(t('JSON copied to clipboard!'));
                        setTimeout(() => setTestResult(''), 2000);
                      }}
                    >
                      <Trans>Copy JSON</Trans>
                    </Button>
                  </View>
                  <Select
                    value={selectedTransactionIdx.toString()}
                    onChange={val =>
                      setSelectedTransactionIdx(parseInt(val, 10))
                    }
                    options={previewTransactions.map((txn, idx) => {
                      const preview = txn as PreviewTransaction;
                      return [
                        idx.toString(),
                        `${preview.transaction_date || t('No date')} - ${
                          preview.creditor?.name ||
                          preview.debtor?.name ||
                          t('Unknown')
                        } - ${preview.transaction_amount?.amount || '0'} ${
                          preview.transaction_amount?.currency || ''
                        }`,
                      ] as [string, string];
                    })}
                  />
                  <View
                    style={{
                      padding: 10,
                      backgroundColor: theme.tableBackground,
                      borderRadius: 4,
                      maxHeight: 150,
                      overflow: 'auto',
                    }}
                  >
                    <pre style={{ fontSize: 11, fontFamily: 'monospace' }}>
                      {JSON.stringify(
                        previewTransactions[selectedTransactionIdx],
                        null,
                        2,
                      )}
                    </pre>
                  </View>
                </View>
              )}

              <View style={{ flexDirection: 'column', gap: 5 }}>
                <Text style={{ fontWeight: 600 }}>
                  <Trans>Template:</Trans>
                </Text>
                <textarea
                  value={template}
                  onChange={e => setTemplate(e.target.value)}
                  placeholder={t('Enter ETA template...')}
                  rows={4}
                  style={{
                    fontFamily: 'monospace',
                    padding: 10,
                    fontSize: 13,
                    width: '100%',
                    border: `1px solid ${theme.formInputBorder}`,
                    borderRadius: 4,
                  }}
                />
              </View>

              {testResult && (
                <View
                  style={{
                    padding: 10,
                    backgroundColor: theme.noticeBackground,
                    borderRadius: 4,
                  }}
                >
                  <Text style={{ fontWeight: 600 }}>
                    <Trans>Test Result:</Trans>
                  </Text>
                  <Text style={{ fontFamily: 'monospace' }}>{testResult}</Text>
                </View>
              )}

              {error && (
                <View
                  style={{
                    padding: 10,
                    backgroundColor: theme.errorBackground,
                    borderRadius: 4,
                  }}
                >
                  <Text style={{ color: theme.errorText }}>{error}</Text>
                </View>
              )}

              <View
                style={{
                  flexDirection: 'row',
                  gap: 10,
                  justifyContent: 'flex-end',
                }}
              >
                <Button
                  variant="bare"
                  onPress={handleTest}
                  isDisabled={loading}
                >
                  <Trans>Test Template</Trans>
                </Button>
                <Button
                  variant="bare"
                  onPress={handleDelete}
                  isDisabled={
                    loading ||
                    !mappings.find(m => m.field_name === selectedField)
                  }
                >
                  <Trans>Delete</Trans>
                </Button>
                <Button
                  variant="primary"
                  onPress={handleSave}
                  isDisabled={loading}
                >
                  <Trans>Save Mapping</Trans>
                </Button>
              </View>

              {actual_account_id && mappings.length > 0 && (
                <View
                  style={{
                    marginTop: 15,
                    padding: 15,
                    backgroundColor: theme.tableBackground,
                    borderRadius: 4,
                  }}
                >
                  <Text style={{ fontWeight: 600, marginBottom: 10 }}>
                    <Trans>Re-apply Mappings to Existing Transactions</Trans>
                  </Text>
                  {reapplyResult && (
                    <View
                      style={{
                        padding: 10,
                        backgroundColor: theme.noticeBackground,
                        borderRadius: 4,
                        marginBottom: 10,
                      }}
                    >
                      <Text style={{ fontSize: 13 }}>{reapplyResult}</Text>
                    </View>
                  )}
                  <View style={{ flexDirection: 'column', gap: 8 }}>
                    <Text
                      style={{
                        fontSize: 12,
                        color: theme.pageTextSubdued,
                      }}
                    >
                      <Trans>
                        Re-fetch transactions from the bank (last 90 days) and
                        re-apply the saved mappings to matching transactions.
                      </Trans>
                    </Text>
                    <Button
                      variant="primary"
                      onPress={handleReapply}
                      isDisabled={reapplyLoading || loading}
                    >
                      {reapplyLoading
                        ? t('Fetching from bank...')
                        : t('Re-fetch from Bank & Apply')}
                    </Button>
                  </View>
                </View>
              )}

              <View style={{ marginTop: 10 }}>
                <Text
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: theme.pageTextSubdued,
                  }}
                >
                  <Trans>Available helpers:</Trans>
                </Text>
                <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                  trim, upper, lower, substring, replace, concat, abs, round,
                  formatDate, ifEmpty, get, join, first, last
                </Text>
              </View>
            </View>
          </Form>
        </>
      )}
    </Modal>
  );
}
